// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import { useCallback } from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { createTheme } from '../../design-system/theme';
import { LeaderboardTemplateCard } from './LeaderboardTemplateCard';
import { leaderboardStrings } from './strings';
import type { LeaderboardTemplate } from './types';

export interface LeaderboardTemplatePickerProps {
  readonly templates: readonly LeaderboardTemplate[];
  readonly selectedTemplateId: string | null;
  readonly onSelect: (template: LeaderboardTemplate | null) => void;
  readonly onUseTemplate: (template: LeaderboardTemplate) => void;
  readonly onStartBlank: () => void;
}

/** Horizontal scroller showing the template carousel with primary CTA. */
export const LeaderboardTemplatePicker: React.FC<LeaderboardTemplatePickerProps> = ({
  templates,
  selectedTemplateId,
  onSelect,
  onUseTemplate,
  onStartBlank,
}) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');

  const selected = templates.find((t) => t.id === selectedTemplateId) ?? null;

  const handleUse = useCallback(() => {
    if (selected !== null) {
      onUseTemplate(selected);
    }
  }, [selected, onUseTemplate]);

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.surface.primary }]}>
      <Text style={[styles.title, { color: theme.colors.text.primary }]}>
        {leaderboardStrings.templatePickerTitle}
      </Text>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {templates.map((template) => (
          <LeaderboardTemplateCard
            key={template.id}
            template={template}
            selected={template.id === selectedTemplateId}
            onPress={onSelect}
          />
        ))}
      </ScrollView>
      <View style={styles.actions}>
        <Pressable
          onPress={onStartBlank}
          accessibilityRole="button"
          accessibilityLabel={leaderboardStrings.customBoardCta}
          style={({ pressed }) => [
            styles.secondaryButton,
            { borderColor: theme.colors.border.primary },
            pressed && { opacity: 0.7 },
          ]}
        >
          <Text style={[styles.secondaryText, { color: theme.colors.text.primary }]}>
            {leaderboardStrings.customBoardCta}
          </Text>
        </Pressable>
        <Pressable
          onPress={handleUse}
          accessibilityRole="button"
          accessibilityLabel={leaderboardStrings.templatePickerCta}
          accessibilityState={{ disabled: selected === null }}
          disabled={selected === null}
          style={({ pressed }) => [
            styles.primaryButton,
            { backgroundColor: theme.colors.accent.indigo },
            pressed && { opacity: 0.85 },
            selected === null && { opacity: 0.4 },
          ]}
        >
          <Text style={styles.primaryText}>{leaderboardStrings.templatePickerCta}</Text>
        </Pressable>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  } satisfies ViewStyle,
  title: {
    fontSize: 17,
    fontWeight: '700',
    marginBottom: 12,
  } satisfies TextStyle,
  scrollContent: {
    flexDirection: 'row',
    paddingVertical: 8,
  } satisfies ViewStyle,
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 16,
  } satisfies ViewStyle,
  primaryButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    marginLeft: 8,
    minWidth: 120,
    alignItems: 'center',
  } satisfies ViewStyle,
  secondaryButton: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
  } satisfies ViewStyle,
  primaryText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#FFFFFF',
  } satisfies TextStyle,
  secondaryText: {
    fontSize: 14,
    fontWeight: '600',
  } satisfies TextStyle,
});
